import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { 
  BarChart3, 
  Users, 
  TrendingUp, 
  TrendingDown,
  Download,
  Calendar,
  Activity,
  Star,
  MessageCircle,
  CreditCard,
  FileText,
  PieChart,
  LineChart
} from 'lucide-react';

interface SystemStats {
  userStats: {
    totalUsers: number;
    newUsersThisMonth: number;
    activeUsers: number;
    blockedUsers: number;
  };
  sessionStats: {
    totalSessions: number;
    completedSessions: number;
    canceledSessions: number;
    averageSessionDuration: number;
  };
  skillStats: {
    totalSkills: number;
    totalCategories: number;
    mostPopularSkills: Array<{
      name: string;
      count: number;
    }>;
  };
  financialStats: {
    totalCredits: number;
    totalTransactions: number;
    averageCreditsPerUser: number;
  };
}

interface ReportData {
  type: string;
  data: any[];
  generatedAt: string;
}

const SystemReports: React.FC = () => {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<string>('overview');
  const [dateRange, setDateRange] = useState<string>('30');
  const [generatedReports, setGeneratedReports] = useState<ReportData[]>([]);

  useEffect(() => {
    fetchSystemStats();
  }, []);

  const fetchSystemStats = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getSystemStats();
      setStats(response.data);
    } catch (error: any) {
      toast.error('Failed to load system statistics');
      console.error('Error fetching system stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateReport = async (reportType: string) => {
    try {
      const response = await apiService.generateReport(reportType, { dateRange });
      const newReport: ReportData = {
        type: reportType,
        data: response.data,
        generatedAt: new Date().toISOString()
      };
      setGeneratedReports(prev => [newReport, ...prev]);
      toast.success(`${reportType} report generated successfully!`);
    } catch (error: any) {
      toast.error('Failed to generate report');
      console.error('Error generating report:', error);
    }
  };

  const downloadReport = async (report: ReportData) => {
    try {
      const response = await apiService.downloadReport(report.type, report.generatedAt);
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.type}_report_${new Date(report.generatedAt).toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded successfully!');
    } catch (error: any) {
      toast.error('Failed to download report');
      console.error('Error downloading report:', error);
    }
  };

  const getReportIcon = (type: string) => {
    switch (type) {
      case 'user_activity': return <Users className="h-5 w-5" />;
      case 'session_analytics': return <Activity className="h-5 w-5" />;
      case 'skill_popularity': return <Star className="h-5 w-5" />;
      case 'financial_summary': return <CreditCard className="h-5 w-5" />;
      case 'chat_analytics': return <MessageCircle className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  const getReportTitle = (type: string) => {
    switch (type) {
      case 'user_activity': return 'User Activity Report';
      case 'session_analytics': return 'Session Analytics Report';
      case 'skill_popularity': return 'Skill Popularity Report';
      case 'financial_summary': return 'Financial Summary Report';
      case 'chat_analytics': return 'Chat Analytics Report';
      default: return 'Custom Report';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Reports</h1>
            <p className="text-gray-600 mt-1">Generate and view system analytics reports</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.userStats.totalUsers}</p>
                <p className="text-xs text-green-600">
                  +{stats.userStats.newUsersThisMonth} this month
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.sessionStats.totalSessions}</p>
                <p className="text-xs text-green-600">
                  {stats.sessionStats.completedSessions} completed
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Star className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Skills</p>
                <p className="text-2xl font-bold text-gray-900">{stats.skillStats.totalSkills}</p>
                <p className="text-xs text-blue-600">
                  {stats.skillStats.totalCategories} categories
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CreditCard className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Credits</p>
                <p className="text-2xl font-bold text-gray-900">{stats.financialStats.totalCredits}</p>
                <p className="text-xs text-purple-600">
                  {stats.financialStats.averageCreditsPerUser} avg per user
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Generation */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Generate Reports</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { type: 'user_activity', title: 'User Activity Report', description: 'User registration, login patterns, and activity metrics' },
              { type: 'session_analytics', title: 'Session Analytics Report', description: 'Session completion rates, duration analysis, and trends' },
              { type: 'skill_popularity', title: 'Skill Popularity Report', description: 'Most popular skills, category analysis, and trends' },
              { type: 'financial_summary', title: 'Financial Summary Report', description: 'Credit transactions, revenue analysis, and financial metrics' },
              { type: 'chat_analytics', title: 'Chat Analytics Report', description: 'Message volume, user engagement, and communication patterns' }
            ].map((report) => (
              <div key={report.type} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-3">
                  {getReportIcon(report.type)}
                  <h3 className="ml-2 font-medium text-gray-900">{report.title}</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">{report.description}</p>
                <button
                  onClick={() => generateReport(report.type)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Generate Report
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Generated Reports */}
      {generatedReports.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Generated Reports</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {generatedReports.map((report, index) => (
                <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getReportIcon(report.type)}
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {getReportTitle(report.type)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Generated on {new Date(report.generatedAt).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">
                        {report.data.length} data points
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => downloadReport(report)}
                      className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Popular Skills Chart */}
      {stats?.skillStats.mostPopularSkills && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Most Popular Skills</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {stats.skillStats.mostPopularSkills.slice(0, 10).map((skill, index) => (
                <div key={skill.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                    <span className="text-sm font-medium text-gray-900">{skill.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(skill.count / stats.skillStats.mostPopularSkills[0].count) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500">{skill.count} users</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemReports; 